import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { chatsApi, ChatType } from '../../api/chats';
import { profilesApi } from '../../api/profiles';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbCheckBox from '../controls/HrnbCheckBox';
import '../../css/chats/MessageSearchResults.css';

/**
 * Дебаунс
 */
function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/**
 * Результаты глобального поиска сообщений по всем чатам.
 * С фильтрами: отправитель, дата с/по, только с вложениями.
 *
 * @param {Object} props
 * @param {string} props.query — поисковый запрос
 * @param {Array} [props.chats] — список чатов (для разрешения названий)
 * @param {Object} [props.chatData] — предзагруженные данные профилей/чатов (Map)
 * @param {Object} [props.currentUser] — текущий пользователь (для DIRECT-чатов)
 * @param {Function} props.onNavigate — колбэк при клике на результат (chatId, messageId)
 */
export default function MessageSearchResults({ query, chats = [], chatData, currentUser, onNavigate }) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const debounceTimer = useRef(null);
    const listRef = useRef(null);

    // ═══════════════════════════════════
    //  Фильтры
    // ═══════════════════════════════════
    const [showFilters, setShowFilters] = useState(false);
    const [senderQuery, setSenderQuery] = useState('');
    const [selectedSender, setSelectedSender] = useState(null); // { id, displayName }
    const [senderSuggestions, setSenderSuggestions] = useState([]);
    const [senderSearching, setSenderSearching] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [hasAttachments, setHasAttachments] = useState(false);
    const senderDebounce = useDebounce(senderQuery, 300);

    // Поиск профилей для автокомплита отправителя
    useEffect(() => {
        if (!senderDebounce || senderDebounce.length < 2) {
            setSenderSuggestions([]);
            return;
        }
        let cancelled = false;
        setSenderSearching(true);
        profilesApi.list({ search: senderDebounce, size: 10 })
            .then(resp => {
                if (cancelled) return;
                const items = resp?.content || resp || [];
                setSenderSuggestions(items);
            })
            .catch(() => {
                if (!cancelled) setSenderSuggestions([]);
            })
            .finally(() => {
                if (!cancelled) setSenderSearching(false);
            });
        return () => { cancelled = true; };
    }, [senderDebounce]);

    const handleSelectSender = useCallback((profile) => {
        setSelectedSender(profile);
        setSenderQuery(profile.displayName || '');
        setSenderSuggestions([]);
    }, []);

    const handleClearSender = useCallback(() => {
        setSelectedSender(null);
        setSenderQuery('');
        setSenderSuggestions([]);
    }, []);

    // Сброс фильтров
    const handleClearFilters = useCallback(() => {
        setSelectedSender(null);
        setSenderQuery('');
        setSenderSuggestions([]);
        setDateFrom('');
        setDateTo('');
        setHasAttachments(false);
    }, []);

    // ═══════════════════════════════════

    // Map chatId → chat object для быстрого поиска
    const chatsMap = useMemo(() => {
        const m = new Map();
        for (const c of chats) m.set(c.id, c);
        return m;
    }, [chats]);

    // Разрешение названия чата по chatId
    const resolveChatName = useCallback((chatId) => {
        const chat = chatsMap.get(chatId);
        if (!chat) return 'Чат';

        // Для DIRECT — имя собеседника из profiles
        if (chat.chatType === ChatType.DIRECT) {
            const currentProfileId = currentUser?.profileId || currentUser?.profiles?.[0]?.id;
            const participants = chat.participantIds || chat.participants || [];
            const otherId = participants.find(pId => pId !== currentProfileId) || participants[0];
            if (otherId && chatData?.profiles) {
                const profile = chatData.profiles.get(otherId);
                if (profile?.displayName) return profile.displayName;
            }
            return chat.title || chat.name || 'Личный чат';
        }

        // Для BATTLE_SUPPORT — название баттла
        if (chat.chatType === ChatType.BATTLE_SUPPORT && chat.battleId && chatData?.battles) {
            const battle = chatData.battles.get(chat.battleId);
            if (battle?.title) return battle.title;
        }

        return chat.title || chat.name || 'Чат';
    }, [chatsMap, chatData, currentUser]);

    const performSearch = useCallback(async (q, filters) => {
        if (!q || q.trim().length < 2) {
            setResults([]);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const messages = await chatsApi.searchMessages(q, {
                senderId: filters?.senderId || null,
                dateFrom: filters?.dateFrom || null,
                dateTo: filters?.dateTo || null,
                hasAttachments: filters?.hasAttachments || null,
            }, 50);
            setResults(messages);
        } catch (err) {
            console.error('[MessageSearchResults] Search failed:', err);
            setError('Не удалось выполнить поиск');
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced поиск с учётом фильтров
    const filtersRef = useRef({});
    filtersRef.current = {
        senderId: selectedSender?.id || null,
        dateFrom: dateFrom ? new Date(dateFrom).toISOString() : null,
        dateTo: dateTo ? new Date(dateTo).toISOString() : null,
        hasAttachments: hasAttachments || null,
    };

    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            performSearch(query, filtersRef.current);
            setFocusedIndex(-1);
        }, 400);
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, selectedSender, dateFrom, dateTo, hasAttachments, performSearch]);

    // Прокрутка к сфокусированному элементу
    useEffect(() => {
        if (focusedIndex < 0) return;
        const listEl = listRef.current;
        if (!listEl) return;
        const item = listEl.children[focusedIndex];
        if (item) {
            item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [focusedIndex]);

    // Клавиатурная навигация
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowDown' && results.length > 0) {
                e.preventDefault();
                setFocusedIndex(prev => prev < 0 ? 0 : Math.min(prev + 1, results.length - 1));
            } else if (e.key === 'ArrowUp' && results.length > 0) {
                e.preventDefault();
                setFocusedIndex(prev => prev <= 0 ? results.length - 1 : prev - 1);
            } else if (e.key === 'Enter' && focusedIndex >= 0 && focusedIndex < results.length) {
                e.preventDefault();
                const msg = results[focusedIndex];
                onNavigate?.(msg.chatId, msg.id);
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [results, focusedIndex, onNavigate]);

    const hasActiveFilters = selectedSender || dateFrom || dateTo || hasAttachments;

    return (
        <div className="message-search-results">
            {/* ═══════ Блок фильтров ═══════ */}
            <div className="message-search-results__filters-bar">
                <HrnbButton
                    variant="light"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`message-search-results__filters-toggle ${hasActiveFilters ? 'message-search-results__filters-toggle--active' : ''}`}
                >
                    {showFilters ? '▲ Скрыть фильтры' : '▼ Фильтры'}
                    {hasActiveFilters && <span className="message-search-results__filters-badge">!</span>}
                </HrnbButton>
                {hasActiveFilters && (
                    <HrnbButton
                        variant="light"
                        size="sm"
                        onClick={handleClearFilters}
                        className="message-search-results__filters-clear"
                    >
                        ✕ Сбросить
                    </HrnbButton>
                )}
            </div>

            {showFilters && (
                <div className="message-search-results__filters">
                    {/* От кого */}
                    <div className="message-search-results__filter-group">
                        <label className="message-search-results__filter-label">От кого</label>
                        <div className="message-search-results__sender-wrapper">
                            <input
                                type="text"
                                className="message-search-results__sender-input"
                                placeholder="Имя отправителя..."
                                value={senderQuery}
                                onChange={(e) => {
                                    setSenderQuery(e.target.value);
                                    if (selectedSender) setSelectedSender(null);
                                }}
                            />
                            {selectedSender && (
                                <button
                                    className="message-search-results__sender-clear"
                                    onClick={handleClearSender}
                                    title="Убрать фильтр"
                                >
                                    ✕
                                </button>
                            )}
                            {senderSuggestions.length > 0 && !selectedSender && (
                                <div className="message-search-results__sender-suggestions">
                                    {senderSearching && <div className="message-search-results__sender-loading">Поиск...</div>}
                                    {senderSuggestions.map(p => (
                                        <div
                                            key={p.id}
                                            className="message-search-results__sender-suggestion"
                                            onClick={() => handleSelectSender(p)}
                                        >
                                            {p.displayName || p.name || 'Пользователь'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Диапазон дат */}
                    <div className="message-search-results__filter-row">
                        <div className="message-search-results__filter-group">
                            <label className="message-search-results__filter-label">Дата с</label>
                            <input
                                type="date"
                                className="message-search-results__date-input"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                max={dateTo || undefined}
                            />
                        </div>
                        <div className="message-search-results__filter-group">
                            <label className="message-search-results__filter-label">Дата по</label>
                            <input
                                type="date"
                                className="message-search-results__date-input"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                min={dateFrom || undefined}
                            />
                        </div>
                    </div>

                    {/* Только с вложениями */}
                    <div className="message-search-results__filter-group">
                        <label className="message-search-results__filter-checkbox">
                            <input
                                type="checkbox"
                                checked={hasAttachments}
                                onChange={(e) => setHasAttachments(e.target.checked)}
                            />
                            <span>Только с вложениями</span>
                        </label>
                    </div>
                </div>
            )}

            {/* ═══════ Результаты ═══════ */}
            {loading && (
                <div className="message-search-results__loading">
                    <HrnbLoader />
                </div>
            )}

            {!loading && error && (
                <div className="message-search-results__error">{error}</div>
            )}

            {!loading && !error && query.trim().length < 2 && (
                <div className="message-search-results__hint">
                    Введите минимум 2 символа для поиска
                </div>
            )}

            {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
                <div className="message-search-results__empty">
                    {hasActiveFilters ? 'Сообщений с такими фильтрами не найдено' : 'Сообщений не найдено'}
                </div>
            )}

            {!loading && !error && results.length > 0 && (
                <div className="message-search-results__list" ref={listRef}>
                    {results.map((msg, index) => {
                        const senderName = msg.sender?.displayName || 'Пользователь';
                        const content = msg.content || '[Вложение]';
                        const timeStr = msg.sentAt
                            ? new Date(msg.sentAt).toLocaleDateString([], { day: '2-digit', month: 'short' }) +
                              ' ' + new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '';

                        // Подсветка совпадения
                        const highlightMatch = (text, q) => {
                            if (!q) return text;
                            const lower = text.toLowerCase();
                            const idx = lower.indexOf(q.toLowerCase());
                            if (idx === -1) return text;
                            return (
                                <>
                                    {text.slice(0, idx)}
                                    <mark className="message-search-results__highlight">
                                        {text.slice(idx, idx + q.length)}
                                    </mark>
                                    {text.slice(idx + q.length)}
                                </>
                            );
                        };

                        return (
                            <div
                                key={msg.id}
                                className={`message-search-results__item ${index === focusedIndex ? 'message-search-results__item--focused' : ''}`}
                                onClick={() => onNavigate?.(msg.chatId, msg.id)}
                            >
                                <div className="message-search-results__item-header">
                                    <span className="message-search-results__chat-name">{resolveChatName(msg.chatId)}</span>
                                    <span className="message-search-results__time">{timeStr}</span>
                                </div>
                                <div className="message-search-results__item-sender">{senderName}</div>
                                <div className="message-search-results__content">
                                    {highlightMatch(content, query)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
